import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api-client';
import { useAuthStore } from '../../stores/authStore';
import type { User } from '@spine-workspace/shared';

export default function OAuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const setAuth = useAuthStore((s) => s.setAuth);
  const [error, setError] = useState('');

  useEffect(() => {
    // Check for provider-level error passed as query param
    const queryError = searchParams.get('error');
    if (queryError) {
      setError(friendlyError(queryError));
      return;
    }

    // Token is in the URL hash: /auth/callback#token=<jwt>
    const hash = window.location.hash.slice(1); // strip leading '#'
    const params = new URLSearchParams(hash);
    const token = params.get('token');

    if (!token) {
      setError('Authentication failed. No token received.');
      return;
    }

    // Store token temporarily so api-client can send it as Authorization header
    useAuthStore.setState({ token, isAuthenticated: false });

    api
      .get<{ user: User }>('/api/auth/me')
      .then(({ user }) => {
        setAuth(user, token);
        navigate('/dashboard', { replace: true });
      })
      .catch(() => {
        useAuthStore.setState({ token: null, isAuthenticated: false });
        setError('Authentication failed. Please try again.');
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) {
    return (
      <div className="min-h-screen bg-bg-deepest flex items-center justify-center p-4">
        <div className="w-full max-w-sm bg-bg-surface border border-border-subtle rounded-xl p-6 shadow-lg text-center">
          <p className="font-sans text-sm text-error mb-4">{error}</p>
          <a
            href="/login"
            className="font-sans text-sm text-accent hover:underline"
          >
            Back to sign in
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg-deepest flex items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
        <p className="font-sans text-sm text-text-secondary">Signing you in…</p>
      </div>
    </div>
  );
}

function friendlyError(code: string): string {
  const map: Record<string, string> = {
    oauth_denied: 'Sign-in was cancelled.',
    oauth_invalid_state: 'Invalid OAuth state. Please try again.',
    oauth_token_failed: 'Failed to complete sign-in. Please try again.',
    oauth_userinfo_failed: 'Could not fetch account info. Please try again.',
    oauth_no_email: 'Your GitHub account has no public/verified email. Please add one and retry.',
    oauth_domain_not_allowed: 'Access is restricted to @eightherastudios.com and @thenicegang.com accounts.',
  };
  return map[code] ?? 'Authentication failed. Please try again.';
}
